// Intuition PDP behaviours: sticky ATC, gallery, size-guide modal.
// Reuses Horizon's product form / ATC / cart drawer — this only adds the chrome.

/* ---- Sticky add-to-cart bar ---- */
class IntuitionStickyAtc extends HTMLElement {
  connectedCallback() {
    this.heroAtc = document.querySelector('.intuition-pdp-hero button[name="add"]');
    this.stickyBtn = this.querySelector('[data-sticky-atc]');
    this.cartOpen = false;

    if (this.stickyBtn && this.heroAtc) {
      this.stickyBtn.addEventListener('click', () => {
        this.heroAtc.click();
        // mirror the hero button's "Added" feedback on the sticky button
        this.stickyBtn.classList.add('is-added');
        clearTimeout(this.addedTimeout);
        this.addedTimeout = setTimeout(() => this.stickyBtn.classList.remove('is-added'), 1500);
      });
    }
    this.#observeHeroAtc();
    this.#watchCartDrawer();
  }

  #observeHeroAtc() {
    if (!this.heroAtc) return;
    this.io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          // Only show once the hero ATC has scrolled ABOVE the viewport (not while
          // it is still below the fold on load), and never while the cart drawer is open.
          const scrolledPast = !entry.isIntersecting && entry.boundingClientRect.top < 0;
          this.classList.toggle('is-visible', scrolledPast && !this.cartOpen);
        }
      },
      { threshold: 0 }
    );
    this.io.observe(this.heroAtc);
  }

  #watchCartDrawer() {
    const dialog = document.querySelector('cart-drawer-component dialog');
    if (!dialog) return;
    const sync = () => {
      this.cartOpen = dialog.open;
      if (dialog.open) this.classList.remove('is-visible');
    };
    this.cartDrawerObserver = new MutationObserver(sync);
    this.cartDrawerObserver.observe(dialog, { attributes: true, attributeFilter: ['open'] });
  }

  disconnectedCallback() {
    this.io?.disconnect();
    this.cartDrawerObserver?.disconnect();
  }
}
if (!customElements.get('intuition-sticky-atc')) {
  customElements.define('intuition-sticky-atc', IntuitionStickyAtc);
}

/* ---- Product gallery: swipe carousel, thumbs/arrows, zoom lightbox, variant sync ---- */
class IntuitionPdpGallery extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('.pdp-gallery__track');
    this.slides = [...this.querySelectorAll('.pdp-gallery__slide')];
    this.thumbs = [...this.querySelectorAll('[data-thumb]')];
    this.dots = [...this.querySelectorAll('[data-dot]')];
    this.prevBtn = this.querySelector('[data-prev]');
    this.nextBtn = this.querySelector('[data-next]');
    this.lightbox = this.querySelector('[data-lightbox]');
    this.lightboxImg = this.querySelector('[data-lightbox-img]');
    this.index = 0;
    if (!this.track || !this.slides.length) return;

    this.thumbs.forEach((t) => t.addEventListener('click', () => this.#goTo(+t.dataset.index)));
    this.dots.forEach((d) => d.addEventListener('click', () => this.#goTo(+d.dataset.index)));
    this.prevBtn?.addEventListener('click', () => this.#goTo(this.index - 1));
    this.nextBtn?.addEventListener('click', () => this.#goTo(this.index + 1));

    // Track which slide is centred as the user swipes.
    this.io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.6) {
            this.#setActive(this.slides.indexOf(entry.target));
          }
        }
      },
      { root: this.track, threshold: [0.6] }
    );
    this.slides.forEach((s) => this.io.observe(s));

    // Zoom lightbox
    this.addEventListener('click', (e) => {
      const zoomBtn = e.target.closest('[data-zoom]');
      if (zoomBtn && this.lightbox && this.lightboxImg) {
        this.lightboxImg.src = zoomBtn.dataset.zoomSrc || zoomBtn.querySelector('img')?.src || '';
        this.lightbox.showModal();
      }
      // Close on the X, on the overlay, or on the dialog backdrop — anywhere but the image itself.
      if (
        e.target.closest('[data-lightbox-close]') ||
        e.target === this.lightbox ||
        e.target.classList.contains('pdp-gallery__lightbox-inner')
      ) {
        this.lightbox?.close();
      }
    });

    this.#buildVariantMap();
    this.#watchVariant();
    this.#setActive(0);
  }

  #goTo(i) {
    const max = this.slides.length - 1;
    this.index = Math.max(0, Math.min(max, i));
    this.slides[this.index]?.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
    this.#setActive(this.index);
  }

  #setActive(i) {
    if (i < 0) return;
    this.index = i;
    this.thumbs.forEach((t) => t.classList.toggle('is-active', +t.dataset.index === i));
    this.dots.forEach((d) => d.classList.toggle('is-active', +d.dataset.index === i));
    if (this.prevBtn) this.prevBtn.hidden = i === 0;
    if (this.nextBtn) this.nextBtn.hidden = i === this.slides.length - 1;
  }

  #buildVariantMap() {
    // media id -> slide index
    this.mediaIndex = new Map();
    this.slides.forEach((s, i) => this.mediaIndex.set(String(s.dataset.mediaId), i));
    // variant id -> media id
    this.variantMedia = new Map();
    try {
      const raw = this.querySelector('[data-variant-media-map]')?.textContent;
      const pairs = raw ? JSON.parse(raw) : [];
      pairs.forEach((p) => { if (p.media != null) this.variantMedia.set(String(p.variant), String(p.media)); });
    } catch (e) { /* no map */ }
  }

  #watchVariant() {
    if (!this.variantMedia.size) return;
    // The hero's product form carries the selected variant id in input[name="id"].
    const sync = () => {
      const input = document.querySelector('.intuition-pdp-hero [name="id"]');
      const variantId = input?.value;
      if (!variantId) return;
      const mediaId = this.variantMedia.get(String(variantId));
      if (mediaId == null) return;
      const idx = this.mediaIndex.get(String(mediaId));
      if (idx != null && idx !== this.index) this.#goTo(idx);
    };
    // Variant pickers dispatch change as the option updates; defer a tick so the form input is current.
    document.addEventListener('change', (e) => {
      if (e.target.closest('variant-picker') || e.target.name === 'id') {
        setTimeout(sync, 0);
      }
    });
  }

  disconnectedCallback() {
    this.io?.disconnect();
  }
}
if (!customElements.get('intuition-pdp-gallery')) {
  customElements.define('intuition-pdp-gallery', IntuitionPdpGallery);
}

/* ---- Document-level behaviours (bind once; survives section morph) ---- */
if (!window.__intuitionPdpBound) {
  window.__intuitionPdpBound = true;

  // Size guide modal
  document.addEventListener('click', (e) => {
    if (e.target.closest('[data-open-size-guide]')) {
      document.querySelector('[data-size-guide-modal]')?.showModal();
    }
    if (e.target.closest('[data-close-size-guide]')) {
      document.querySelector('[data-size-guide-modal]')?.close();
    }
  });
  // Click on backdrop closes the modal
  document.addEventListener('click', (e) => {
    const modal = document.querySelector('[data-size-guide-modal][open]');
    if (modal && e.target === modal) modal.close();
  });

  // Rating links smooth-scroll to the Judge.me widget, falling back to the
  // cross-sell anchor if the app block is not on the template.
  document.addEventListener('click', (e) => {
    const link = e.target.closest('a[href="#reviews"]');
    if (!link) return;
    const target = document.getElementById('judgeme_product_reviews') || document.getElementById('reviews');
    if (!target) return;
    e.preventDefault();
    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  });

  // Keep the hero price + sticky bar in sync with the selected variant.
  // The hero price/sticky markup is custom, so Horizon's variant morph doesn't touch it.
  const readVariantMap = () => {
    try {
      return JSON.parse(document.querySelector('[data-pdp-variants]')?.textContent || '[]');
    } catch (e) {
      return [];
    }
  };
  let lastSyncedId = null;
  const syncVariant = () => {
    const id = document.querySelector('.intuition-pdp-hero [name="id"]')?.value;
    if (!id) return;
    lastSyncedId = id;
    const variant = readVariantMap().find((v) => String(v.id) === String(id));
    if (!variant) return;

    const now = document.querySelector('.pdp-price__now');
    if (now) now.textContent = variant.price;
    const was = document.querySelector('.pdp-price__was');
    if (was) {
      if (variant.compare) {
        was.textContent = variant.compare;
        was.hidden = false;
      } else {
        was.hidden = true;
      }
    }
    const stickyPrice = document.querySelector('.pdp-sticky__price');
    if (stickyPrice) stickyPrice.textContent = variant.price;

    // reflect availability in the sticky add-to-cart button
    const stickyAtc = document.querySelector('.pdp-sticky__atc');
    if (stickyAtc) {
      const soldOut = variant.available === false;
      stickyAtc.disabled = soldOut;
      stickyAtc.textContent = soldOut ? 'Sold Out' : 'Add to Cart';
    }

    // Back-in-stock trigger. Only rendered on `restockable` products, so its presence is the
    // feature flag. Horizon's own Sold Out button is left alone and sits above it.
    const bisTrigger = document.querySelector('[data-bis-open]');
    if (bisTrigger) {
      bisTrigger.hidden = variant.available !== false;
      bisTrigger.dataset.variantId = variant.id;
      bisTrigger.dataset.variantTitle = variant.title || '';
    }

    // mirror the hero picker's selected options into the sticky selects
    document.querySelectorAll('.intuition-pdp-hero select[name^="options["]').forEach((mainSel) => {
      const optionName = mainSel.name.slice(8, -1); // options[NAME]
      const stickySel = document.querySelector(`.pdp-sticky [data-sticky-option="${optionName}"]`);
      if (stickySel && stickySel.value !== mainSel.value) stickySel.value = mainSel.value;
    });
  };
  // Horizon re-renders the section to change variant, so the hidden [name="id"] still holds the
  // previous value for roughly a second after the picker fires. Reading it on the next tick gave
  // the price, sticky bar and back-in-stock trigger one-selection-stale data, so wait for the
  // value to actually change before syncing.
  const syncWhenSettled = () => {
    let waited = 0;
    const tick = () => {
      const id = document.querySelector('.intuition-pdp-hero [name="id"]')?.value || null;
      if (id && id !== lastSyncedId) {
        syncVariant();
        return;
      }
      waited += 50;
      if (waited < 2500) setTimeout(tick, 50);
    };
    setTimeout(tick, 0);
  };
  document.addEventListener('change', (e) => {
    if (e.target.closest('variant-picker') || e.target.name === 'id') syncWhenSettled();
  });

  // Sticky bar selects drive the hero picker (which then updates price + variant everywhere).
  document.addEventListener('change', (e) => {
    const stickySel = e.target.closest('[data-sticky-option]');
    if (!stickySel) return;
    const optionName = stickySel.dataset.stickyOption;
    const mainSel = document.querySelector(`.intuition-pdp-hero select[name="options[${optionName}]"]`);
    if (mainSel && mainSel.value !== stickySel.value) {
      mainSel.value = stickySel.value;
      mainSel.dispatchEvent(new Event('change', { bubbles: true }));
    }
  });

  // initial sync so a first-load sold-out variant is reflected in the sticky bar
  if (document.readyState !== 'loading') syncVariant();
  else document.addEventListener('DOMContentLoaded', syncVariant);

  // Back in stock. Klaviyo's installer only supports a handful of free themes, so rather than
  // let it inject its own button we post to its client endpoint, which takes the public site
  // ID and is meant to be called from the browser. No private key, no app block.
  const bisModal = document.querySelector('[data-bis-modal]');
  if (bisModal) {
    const bisForm = bisModal.querySelector('[data-bis-form]');
    const bisStatus = bisModal.querySelector('[data-bis-status]');
    const bisVariantLabel = bisModal.querySelector('[data-bis-variant]');
    const bisEmail = bisModal.querySelector('input[type="email"]');
    const bisSubmit = bisModal.querySelector('[data-bis-submit]');
    let bisVariantId = null;

    const setBisStatus = (message, state) => {
      bisStatus.textContent = message;
      if (state) bisStatus.dataset.state = state;
      else delete bisStatus.dataset.state;
    };

    document.addEventListener('click', (e) => {
      const open = e.target.closest('[data-bis-open]');
      if (open) {
        bisVariantId = open.dataset.variantId || null;
        const label = open.dataset.variantTitle || '';
        if (bisVariantLabel) bisVariantLabel.textContent = label ? `, ${label}` : '';
        bisForm.hidden = false;
        bisForm.reset();
        setBisStatus('', null);
        bisModal.showModal();
        bisEmail?.focus();
        return;
      }
      if (e.target.closest('[data-bis-close]')) bisModal.close();
    });

    // click on the backdrop closes, same as the size guide
    bisModal.addEventListener('click', (e) => {
      if (e.target === bisModal) bisModal.close();
    });

    bisForm.addEventListener('submit', async (e) => {
      e.preventDefault();
      const email = (bisEmail?.value || '').trim();
      if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        setBisStatus('Enter a valid email address.', 'error');
        bisEmail?.focus();
        return;
      }
      if (!bisVariantId) {
        setBisStatus('Choose a size first.', 'error');
        return;
      }
      const key = bisModal.dataset.klaviyoKey;
      if (!key) {
        setBisStatus('Notifications are not set up yet. Please contact us and we will let you know.', 'error');
        return;
      }

      bisSubmit.disabled = true;
      setBisStatus('Signing you up...', null);
      try {
        const res = await fetch(
          `https://a.klaviyo.com/client/back-in-stock-subscriptions?company_id=${encodeURIComponent(key)}`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/vnd.api+json', revision: '2026-07-15' },
            body: JSON.stringify({
              data: {
                type: 'back-in-stock-subscription',
                attributes: {
                  channels: ['EMAIL'],
                  profile: { data: { type: 'profile', attributes: { email } } },
                },
                relationships: {
                  // Klaviyo keys Shopify variants as $shopify:::$default:::<variant id>
                  variant: {
                    data: { type: 'catalog-variant', id: `$shopify:::$default:::${bisVariantId}` },
                  },
                },
              },
            }),
          }
        );
        if (res.ok) {
          bisForm.hidden = true;
          setBisStatus('Done. We will email you when this size is back.', 'ok');
        } else {
          setBisStatus('That did not go through. Please try again in a moment.', 'error');
        }
      } catch (err) {
        setBisStatus('That did not go through. Please check your connection and try again.', 'error');
      } finally {
        bisSubmit.disabled = false;
      }
    });
  }
}

/* ---- Custom OOS-aware variant dropdowns (buy box) ----
   The native variant picker stays the engine + no-JS fallback. We render an accessible listbox into
   [data-cdd-mount] (which lives OUTSIDE <variant-picker>, so Horizon's morph never strips it) and
   rebuild it whenever the picker morphs. Selecting proxies value + a bubbling change to the hidden
   select, so variant-picker.js / ATC / price / gallery / sticky all keep working unchanged. */
if (!window.__intuitionCddBound) {
  window.__intuitionCddBound = true;

  const cleanLabel = (opt) => (opt.value || opt.textContent || '').trim();
  const getSelects = () => [...document.querySelectorAll('.pdp-buybox .variant-option__select')];
  const selectByName = (name) =>
    document.querySelector(`.pdp-buybox .variant-option__select[name="options[${name}]"]`);

  let rebuildTimer = null;
  let pendingFocusName = null;
  const observers = [];

  function build() {
    const variants = document.querySelector('.pdp-variants');
    const mount = document.querySelector('.pdp-buybox [data-cdd-mount]');
    const selects = getSelects();
    if (!variants || !mount || !selects.length) return;

    // `restockable` tag (data-restockable on the hero section) flips the OOS tag to a notify link.
    const restockable = document.querySelector('.intuition-pdp-hero')?.dataset.restockable === 'true';

    mount.innerHTML = '';
    selects.forEach((select) => {
      const name = select.name.replace(/^options\[/, '').replace(/\]$/, '');
      const base = `pdp-cdd-${name.replace(/[^a-z0-9]+/gi, '-')}`;
      const cdd = document.createElement('div');
      cdd.className = 'pdp-cdd';
      cdd.dataset.optionName = name;

      const label = document.createElement('span');
      label.className = 'pdp-cdd__label';
      label.id = `${base}-label`;
      label.textContent = name;
      cdd.appendChild(label);

      const selectedOpt = select.options[select.selectedIndex];
      const trigger = document.createElement('button');
      trigger.type = 'button';
      trigger.className = 'pdp-cdd__trigger';
      trigger.setAttribute('aria-haspopup', 'listbox');
      trigger.setAttribute('aria-expanded', 'false');
      trigger.setAttribute('aria-labelledby', `${base}-label ${base}-value`);
      trigger.innerHTML =
        `<span id="${base}-value" class="pdp-cdd__value">${selectedOpt ? cleanLabel(selectedOpt) : ''}</span>` +
        `<svg class="pdp-cdd__caret" viewBox="0 0 10 6" aria-hidden="true"><path d="M1 1l4 4 4-4" fill="none" stroke="currentColor" stroke-width="1.6"/></svg>`;
      cdd.appendChild(trigger);

      const list = document.createElement('ul');
      list.className = 'pdp-cdd__list';
      list.id = `${base}-list`;
      list.setAttribute('role', 'listbox');
      list.setAttribute('aria-labelledby', `${base}-label`);
      list.tabIndex = -1;
      trigger.setAttribute('aria-controls', list.id);

      [...select.options].forEach((opt, oi) => {
        const li = document.createElement('li');
        li.id = `${base}-opt-${oi}`;
        li.className = 'pdp-cdd__option';
        li.setAttribute('role', 'option');
        li.dataset.value = opt.value;
        const oos = opt.dataset.available === 'false';
        li.setAttribute('aria-selected', oi === select.selectedIndex ? 'true' : 'false');
        if (oos) {
          li.classList.add('is-oos');
          li.setAttribute('aria-disabled', 'true');
          if (restockable) {
            li.classList.add('is-restockable');
            // Placeholder affordance until Klaviyo Back-in-Stock is wired. Kept as a non-interactive
            // <span> (not a link) so it stays valid inside role="option". When the BIS feature lands,
            // move the trigger OUTSIDE the listbox option (own button/dialog) so it's keyboard-operable.
            li.innerHTML = `<span>${cleanLabel(opt)}</span><span class="pdp-cdd__notify" data-notify-bis>Notify me when back in stock</span>`;
          } else {
            li.innerHTML = `<span>${cleanLabel(opt)}</span><span class="pdp-cdd__oos-tag">Sold out</span>`;
          }
        } else {
          li.innerHTML = `<span>${cleanLabel(opt)}</span>`;
        }
        list.appendChild(li);
      });
      cdd.appendChild(list);
      mount.appendChild(cdd);
    });

    variants.classList.add('cdd-ready');
    observePickers();

    // keep keyboard focus on the just-changed control after a morph-driven rebuild
    if (pendingFocusName) {
      const cdd = mount.querySelector(`.pdp-cdd[data-option-name="${pendingFocusName}"]`);
      cdd?.querySelector('.pdp-cdd__trigger')?.focus();
      pendingFocusName = null;
    }
  }

  function observePickers() {
    observers.forEach((o) => o.disconnect());
    observers.length = 0;
    document.querySelectorAll('.pdp-buybox variant-picker').forEach((picker) => {
      const o = new MutationObserver(() => {
        clearTimeout(rebuildTimer);
        rebuildTimer = setTimeout(build, 30);
      });
      o.observe(picker, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['selected', 'data-available'],
      });
      observers.push(o);
    });
  }

  const optionsOf = (cdd) => [...cdd.querySelectorAll('.pdp-cdd__option')];
  const selectableOf = (cdd) => optionsOf(cdd).filter((li) => !li.classList.contains('is-oos'));

  function setActive(cdd, li) {
    if (!li) return;
    optionsOf(cdd).forEach((o) => o.classList.remove('is-active'));
    li.classList.add('is-active');
    cdd.querySelector('.pdp-cdd__list')?.setAttribute('aria-activedescendant', li.id);
    li.scrollIntoView({ block: 'nearest' });
  }

  function closeCdd(cdd, focusTrigger) {
    cdd.classList.remove('is-open');
    const trigger = cdd.querySelector('.pdp-cdd__trigger');
    trigger?.setAttribute('aria-expanded', 'false');
    if (focusTrigger) trigger?.focus();
  }

  function closeAll(except) {
    document.querySelectorAll('.pdp-cdd.is-open').forEach((c) => {
      if (c !== except) closeCdd(c, false);
    });
  }

  function openCdd(cdd) {
    closeAll(cdd);
    cdd.classList.add('is-open');
    cdd.querySelector('.pdp-cdd__trigger')?.setAttribute('aria-expanded', 'true');
    const current = cdd.querySelector('.pdp-cdd__option[aria-selected="true"]');
    setActive(cdd, current && !current.classList.contains('is-oos') ? current : selectableOf(cdd)[0]);
    cdd.querySelector('.pdp-cdd__list')?.focus();
  }

  function choose(cdd, li) {
    if (!li) return;
    // Sold-out options stay unselectable, except on restockable products: selecting one is how
    // you reach that size's back-in-stock signup, so blocking it strands every size but the first.
    if (li.classList.contains('is-oos') && !li.classList.contains('is-restockable')) return;
    const name = cdd.dataset.optionName;
    const select = selectByName(name);
    closeCdd(cdd, true);
    if (!select || select.value === li.dataset.value) return;
    const valEl = cdd.querySelector('.pdp-cdd__value');
    if (valEl) valEl.textContent = li.querySelector('span')?.textContent || li.dataset.value;
    pendingFocusName = name;
    select.value = li.dataset.value;
    select.dispatchEvent(new Event('change', { bubbles: true }));
  }

  document.addEventListener('click', (e) => {
    // The "Notify me when back in stock" text inside a listbox option is a label, not a control.
    // Clicking it selects the option like any other, which reveals the real trigger in the buy box.
    const trigger = e.target.closest('.pdp-cdd__trigger');
    if (trigger) {
      const cdd = trigger.closest('.pdp-cdd');
      cdd.classList.contains('is-open') ? closeCdd(cdd, false) : openCdd(cdd);
      return;
    }
    const opt = e.target.closest('.pdp-cdd__option');
    if (opt) {
      // Clicking the "Notify me" label picks that variant and goes straight to the modal.
      // Horizon swaps the hidden [name="id"] asynchronously, so the trigger still carries the
      // previous variant for a few frames. Wait for the id to actually change before opening,
      // or the modal names the wrong size.
      const wantsNotify = e.target.closest('[data-notify-bis]') !== null;
      const wasSelected = opt.getAttribute('aria-selected') === 'true';
      const beforeId = document.querySelector('[data-bis-open]')?.dataset.variantId;
      choose(opt.closest('.pdp-cdd'), opt);
      if (wantsNotify) {
        let waited = 0;
        const openWhenReady = () => {
          const t = document.querySelector('[data-bis-open]');
          if (!t) return;
          if (wasSelected || t.dataset.variantId !== beforeId || waited >= 2500) {
            t.click();
            return;
          }
          waited += 50;
          setTimeout(openWhenReady, 50);
        };
        setTimeout(openWhenReady, 50);
      }
      return;
    }
    if (!e.target.closest('.pdp-cdd')) closeAll(null);
  });

  document.addEventListener('keydown', (e) => {
    const cdd = e.target.closest('.pdp-cdd');
    if (!cdd) return;
    const onTrigger = !!e.target.closest('.pdp-cdd__trigger');
    const isOpen = cdd.classList.contains('is-open');

    if (onTrigger && !isOpen && ['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(e.key)) {
      e.preventDefault();
      openCdd(cdd);
      return;
    }
    if (!isOpen) return;

    const sel = selectableOf(cdd);
    const active = cdd.querySelector('.pdp-cdd__option.is-active');
    const pos = sel.indexOf(active);

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setActive(cdd, sel[Math.min(sel.length - 1, pos + 1)] || sel[0]);
        break;
      case 'ArrowUp':
        e.preventDefault();
        setActive(cdd, sel[Math.max(0, pos - 1)] || sel[sel.length - 1]);
        break;
      case 'Home':
        e.preventDefault();
        setActive(cdd, sel[0]);
        break;
      case 'End':
        e.preventDefault();
        setActive(cdd, sel[sel.length - 1]);
        break;
      case 'Enter':
      case ' ':
        e.preventDefault();
        choose(cdd, active);
        break;
      case 'Escape':
        e.preventDefault();
        closeCdd(cdd, true);
        break;
      case 'Tab':
        closeCdd(cdd, false);
        break;
      default:
        if (e.key.length === 1 && /\S/.test(e.key)) {
          const match = sel.find((li) => (li.textContent || '').trim().toLowerCase().startsWith(e.key.toLowerCase()));
          if (match) setActive(cdd, match);
        }
    }
  });

  if (document.readyState !== 'loading') build();
  else document.addEventListener('DOMContentLoaded', build);
}
